
import { useEffect, useState } from 'react';
import { useSearchParams, useRouter } from '@/lib/router-compat';
import { teamApi } from '@/lib/api/team';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks/usePageTitle';
import type { InviteInfo } from '@/types/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Building2, XCircle } from 'lucide-react';
import { SettingsFormSkeleton } from '@/components/ui/skeletons';
import { PasswordRequirements, passwordMeetsRequirements } from '@/components/ui/password-requirements';

function AcceptInvitePage() {
  usePageTitle('Accept Invitation');
  const searchParams = useSearchParams();
  const router = useRouter();
  const { login, switchOrganization } = useAuth();
  const token = searchParams.get("token") ?? "";

  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(false);

  useEffect(() => {
    async function loadInfo() {
      try {
        const data = await teamApi.getInviteInfo(token);
        setInfo(data);
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          setIsExistingUser(true);
        }
      } catch {
        setError('This invitation is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    }
    loadInfo();
  }, [token]);

  async function handleAccept() {
    if (!isExistingUser && !passwordMeetsRequirements(password)) {
      setError('Password must be at least 8 characters and contain a number');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const result = await teamApi.acceptInvite(token, isExistingUser ? undefined : password);

      if (isExistingUser) {
        await switchOrganization(result.organizationId);
        router.push('/dashboard');
      } else {
        await login({ email: info!.email, password });
      }
    } catch (err: unknown) {
      const apiErr = err as { message?: string; details?: { message?: string } };
      setError(apiErr.details?.message || apiErr.message || 'Failed to accept invitation');
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center p-4">
        <SettingsFormSkeleton rows={1} />
      </div>
    );
  }

  if (!info) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="mx-auto h-12 w-12 text-destructive mb-2" />
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button variant="outline" onClick={() => router.push('/login')}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Building2 className="mx-auto h-10 w-10 text-primary mb-2" />
          <CardTitle>You&apos;re Invited!</CardTitle>
          <CardDescription>
            You&apos;ve been invited to join an organization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-lg border p-4 text-center space-y-2">
            <p className="text-lg font-semibold">{info.organizationName}</p>
            <div className="flex items-center justify-center gap-2">
              <span className="text-sm text-muted-foreground">Role:</span>
              <Badge variant="secondary">{info.role}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Invited as {info.email}
            </p>
          </div>

          {!isExistingUser && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Create a password to set up your account:
              </p>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAccept()}
                  placeholder="Enter password"
                />
                <PasswordRequirements password={password} />
              </div>
            </div>
          )}

          {isExistingUser && (
            <p className="text-sm text-muted-foreground text-center">
              You&apos;re already logged in. Click below to accept the invitation.
            </p>
          )}

          {error && (
            <p className="text-sm text-destructive text-center">{error}</p>
          )}

          <Button
            onClick={handleAccept}
            disabled={submitting}
            className="w-full"
            size="lg"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isExistingUser ? 'Accept Invitation' : 'Create Account & Join'}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Already have an account?{' '}
            <button
              onClick={() => {
                setIsExistingUser(true);
              }}
              className="text-primary underline"
            >
              I already have an account
            </button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/invite')({ component: AcceptInvitePage });
